/**
 * Same-title detection for outline additions (spec §2-C3).
 *
 * The backend WARNS on a colliding title and still accepts it — rejecting would
 * be inconsistent with the manual Add Section path, which mints colliding
 * `Untitled Section` rows freely. But the warning must reach the reviewer
 * **before** they accept, on the row itself: `getSectionStatus`'s title fallback
 * (backend `bid-artifacts.ts:331-380`) matches draft segments by normalised
 * title when `sectionKey` misses, so a brand-new empty section inherits the old
 * one's draft status *and* word count and reads as "the AI already wrote this".
 *
 * ⚠ `normaliseSectionTitle` must stay byte-identical to the backend's
 * (`backend/src/lib/section-title.ts`) or the warning fires on a different set
 * of titles than the one that actually cross-wires.
 */

/**
 * Lowercase, strip punctuation to spaces, collapse runs, trim.
 *
 * `title` is `unknown` on purpose: `outline_json.sections` also holds
 * questionnaire questions (`prompt`, no `title`). A missing title normalises to
 * '', which matches nothing.
 */
export function normaliseSectionTitle(title: unknown): string {
  if (typeof title !== 'string') return ''
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function duplicateTitleMessage(title: string): string {
  return `Another section is already called “${title}”. Same-titled sections can pick up each other’s draft status and word count — rename one before you add it.`
}

/**
 * Warnings keyed by proposed-section key. `seen` accumulates, so two identical
 * additions in the same batch warn on the second — matching the backend's
 * `collectDuplicateTitleWarnings`.
 */
export function findDuplicateTitleWarnings(
  existingTitles: ReadonlyArray<string | null | undefined>,
  proposed: Array<{ key: string; title: string }>
): Map<string, string> {
  const seen = new Set(existingTitles.map(normaliseSectionTitle))
  const warnings = new Map<string, string>()
  for (const section of proposed) {
    const normalised = normaliseSectionTitle(section.title)
    if (normalised.length > 0 && seen.has(normalised)) {
      warnings.set(section.key, duplicateTitleMessage(section.title))
    }
    seen.add(normalised)
  }
  return warnings
}
